import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import OfferFormSection from './components/OfferFormSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://readybuyergroup.com.au';

const sellersSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/sellers#webpage`,
      url: `${SITE_URL}/sellers`,
      name: 'SureBuy Program for Melbourne Sellers | ReadyBuyer Group | Zero Fees',
      description:
        'Skip open homes and agent fees. The SureBuy Program delivers a written offer in 3-5 business days anywhere in Melbourne. Zero fees, zero commission, settlement on your timeline.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      inLanguage: 'en-AU',
    },
    {
      '@type': 'Service',
      '@id': `${SITE_URL}/sellers#service`,
      name: 'SureBuy Program',
      provider: {
        '@type': 'RealEstateAgent',
        name: 'ReadyBuyer Group',
        url: SITE_URL,
      },
      description:
        'A direct property buying service for Melbourne sellers. Receive a formal written offer within 3-5 business days. No agent fees, no open homes, no upfront costs. Settlement on your timeline.',
      areaServed: {
        '@type': 'City',
        name: 'Melbourne',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'AUD',
        description: 'Zero fees, zero commission',
      },
      serviceType: 'Direct Property Purchase',
      termsOfService: `${SITE_URL}/sellers`,
    },
    {
      '@type': 'HowTo',
      name: 'How the SureBuy Program Works',
      description: 'Four simple steps to sell your Melbourne property with ReadyBuyer Group — no open homes, no agent fees.',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Get in touch',
          text: 'Call, email, or fill in our contact form. Tell us about your property — no obligation, no pressure.',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'We visit your property',
          text: 'A SureBuy agent comes to you anywhere in Melbourne. We assess the property fairly, as-is. No staging required.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Receive a written offer',
          text: 'We deliver a formal written offer within 3-5 business days. The offer is valid for 48 hours.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Accept & settle',
          text: 'Accept the offer and we handle the rest. We work to your timeline.',
        },
      ],
    },
  ],
};

const steps = [
  {
    num: '01',
    title: 'Get in touch',
    desc: 'Call, email, or fill in our contact form. Tell us about your property — no obligation, no pressure.',
    tag: 'Day 1',
  },
  {
    num: '02',
    title: 'We visit your property',
    desc: 'A SureBuy agent comes to you anywhere in Melbourne. We assess the property fairly, as-is. No staging required.',
    tag: 'Days 1–2',
  },
  {
    num: '03',
    title: 'Receive a written offer',
    desc: 'We deliver a formal written offer within 3–5 business days. The offer is valid for 48 hours — giving you time to decide without pressure.',
    tag: 'Days 3–5',
  },
  {
    num: '04',
    title: 'Accept & settle',
    desc: 'Accept the offer and we handle the rest. We work to your timeline — whether you need to move fast or need a few weeks to plan.',
    tag: 'Your timeline',
  },
];

export default function SellersPage() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    document.title = 'SureBuy Program for Melbourne Sellers | ReadyBuyer Group | Zero Fees';
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', 'Skip open homes and agent fees. The SureBuy Program delivers a written offer in 3-5 business days anywhere in Melbourne. Zero fees, zero commission, settlement on your timeline.');

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-sellers';
    script.textContent = JSON.stringify(sellersSchema);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (descEl) descEl.setAttribute('content', prevDesc);
      document.getElementById('schema-sellers')?.remove();
    };
  }, []);

  const scrollToContact = () => {
    document.querySelector('#offer-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/50cd60f4e095c3cff627cddbae7c1386.jpg"
            alt="Melbourne home"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/70 to-gray-950/30"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">For Property Sellers</p>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6 max-w-2xl">
            The SureBuy<br />Program
          </h1>
          <p className="text-gray-300 text-xl leading-relaxed mb-10 max-w-xl">
            Skip the open homes, the agent fees, and the months of waiting. Get a fair written offer in 3 - 5 business days — and sell on your terms.
          </p>
          <button
            onClick={scrollToContact}
            className="px-8 py-4 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap text-base inline-flex items-center gap-2"
          >
            Request a SureBuy Assessment
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* The problem */}
      <section className="py-20 md:py-28 bg-gray-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-5">The Traditional Way</p>
              <h2 className="text-4xl md:text-5xl font-extrabold leading-tight mb-8">
                Selling a home<br />costs you more<br />
                <span className="text-teal-400">than you think.</span>
              </h2>
              <p className="text-gray-400 text-xl leading-relaxed mb-6">
                The average time from deciding to sell to receiving your money is around 6 months. And you&apos;re out of pocket the whole time.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                On a $1M property, agent commission alone runs $22,000–$35,000. Add marketing, staging, cleaning, and lost rent — and the real cost is far higher.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { label: 'Vacate & prep the property', cost: 'Weeks of your time' },
                { label: 'Painting, cleaning & staging', cost: '$3,000–$8,000+' },
                { label: 'Marketing campaign', cost: '$2,000–$5,000+' },
                { label: 'Open homes over 4+ weeks', cost: 'Lost rent & disruption' },
                { label: 'Agent commission (2.2%–3.5%)', cost: '$22,000+ on $1M' },
                { label: 'Wait 60–90 days for settlement', cost: 'No money for months' },
                { label: 'Ongoing mortgage repayments', cost: '$3,000–$18,000*' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-close-circle-line text-red-400 text-base"></i>
                    </div>
                    <span className="text-gray-300 text-base">{item.label}</span>
                  </div>
                  <span className="text-red-400 text-sm font-semibold whitespace-nowrap ml-4">{item.cost}</span>
                </div>
              ))}
              <p className="text-gray-500 text-xs mt-4">* Based on a 12-week selling cycle</p>
            </div>
          </div>
        </div>
      </section>

      {/* What SureBuy gives you */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-5">The SureBuy Way</p>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-950 leading-tight mb-8">
                What the SureBuy<br />Program gives you
              </h2>
              <div className="space-y-7">
                {[
                  {
                    icon: 'ri-calendar-check-line',
                    title: 'A written offer in 3–5 business days',
                    desc: 'Our agent visits your property, does a fair assessment, and delivers a formal written offer. No waiting weeks for a buyer to appear.',
                  },
                  {
                    icon: 'ri-map-pin-2-line',
                    title: 'We come to you — anywhere in Melbourne',
                    desc: "You don't need to vacate, stage, or prep. We assess the property as-is, wherever it is.",
                  },
                  {
                    icon: 'ri-time-line',
                    title: 'Settlement on your timeline',
                    desc: 'Whether you need to move quickly or need time to plan your next step — we work around you.',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Certainty from day one',
                    desc: 'No open homes. No hoping a buyer shows up. No falling through at the last minute. Just a clear, written offer.',
                  },
                  {
                    icon: 'ri-money-dollar-circle-line',
                    title: 'Zero fees. Zero commission.',
                    desc: 'What we offer is what you receive. No deductions, no surprises.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-teal-700 text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1.5 text-lg">{item.title}</h3>
                      <p className="text-gray-500 text-base leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20inviting%20Melbourne%20home%20interior%20living%20room%2C%20natural%20light%20streaming%20through%20large%20windows%2C%20neutral%20tones%2C%20comfortable%20furniture%2C%20potted%20plants%2C%20clean%20and%20modern%20Australian%20home%20styling%2C%20soft%20afternoon%20light&width=700&height=600&seq=seller-interior-02&orientation=portrait"
                alt="Melbourne home interior"
                className="w-full h-[480px] object-cover object-top rounded-2xl mb-6"
              />
              <div className="p-6 bg-[#F4F9F8] rounded-2xl">
                <p className="text-gray-600 text-lg leading-relaxed italic mb-4">
                  &ldquo;A $1M property sold the traditional way costs the seller around $22,000 in agent fees alone — before marketing, staging, or months of lost rent.&rdquo;
                </p>
                <p className="text-sm font-bold text-teal-700 uppercase tracking-wide">With SureBuy — that&apos;s $0.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Situations */}
      <section className="py-16 bg-[#F4F9F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-6">We help sellers in all situations</p>
          <div className="flex flex-wrap gap-3">
            {['Downsizing', 'Upgrading', 'Moving to a care home', 'Divorce or property settlement', 'Financial pressure', 'Deceased estate', 'Relocation', 'Any unique circumstance'].map((tag) => (
              <span key={tag} className="px-5 py-2.5 bg-white text-gray-700 text-base rounded-full font-medium border border-gray-200">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-5">How It Works</p>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-950 leading-tight mb-6">
                Four steps.<br />No surprises.
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed mb-8">
                From first contact to settlement, the process is straightforward. We keep you informed at every step and never leave you guessing.
              </p>
              <button
                onClick={scrollToContact}
                className="px-8 py-4 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap text-base inline-flex items-center gap-2"
              >
                Get Started Today
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="lg:col-span-2 space-y-0">
              {steps.map((step, i) => (
                <div key={step.num} className={`flex gap-8 py-10 ${i < steps.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <div className="flex-shrink-0">
                    <span className="text-4xl font-extrabold text-gray-100">{step.num}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-gray-950">{step.title}</h3>
                      <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-3 py-1 rounded-full whitespace-nowrap">{step.tag}</span>
                    </div>
                    <p className="text-gray-500 text-base leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Get a Free Offer Form */}
      <OfferFormSection />

      <Footer />
    </div>
  );
}
